import _ from "lodash";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import type { FilterType, SortableType } from "@/components/@types";
import type { ViewMode } from "@/components/organisms/DataView";
import {
  detailTypenameForView,
  detailViewForTypename,
} from "@/app/app/_/layouts/nodePanelConfig";
import { Values } from "@/constants";
import {
  idToIdentifier,
  idToTypename,
  identifierAndTypenameToId,
} from "@/objects/@utils";
import { State } from "@/state";
import { getReportsTabKey } from "@/app/app/_/views/ReportsView/ReportsView.utils";
import type {
  AppViewsType,
  AppViewTabModeType,
  CoreStateType,
  GlobalStoreStateType,
} from "@/state/@types";

// URL panel encoding. Legacy values ``"hidden" | "expanded" | "node"`` are
// still accepted on parse for backwards-compatible deep links, but only
// ``"node" | "thread" | "both" | "none"`` are emitted by ``buildAppUrl``.
type PanelStateType = "node" | "thread" | "both" | "none";

/**
 * The thread opens beside a page unless asked not to - but not on a phone's
 * screen or a window as narrow (``useDevice``'s ``isNarrow``), where it would
 * cover the page: there a page opens with nothing over it.
 */
const isPhoneWidth = () =>
  typeof window !== "undefined" && window.innerWidth <= 720;
type LegacyPanelStateType =
  | PanelStateType
  | "hidden"
  | "expanded"
  | "node"
  | "thread";

function panelFromStoreFlags(
  nodeOpen: boolean,
  threadOpen: boolean,
): PanelStateType {
  if (nodeOpen && threadOpen) return "both";
  if (nodeOpen) return "node";
  if (threadOpen) return "thread";
  return "none";
}

/** Panel query for ``navigateToView`` — slide-out thread state comes from the store. */
function resolveNavigatePanel(
  targetView: AppViewsType,
  options: {
    preservePanel: boolean | undefined;
    isSameView: boolean;
    currentRoute: AppRouteState;
  },
): PanelStateType {
  if (options.preservePanel === false) {
    return targetView === "thread" || isPhoneWidth() ? "none" : "thread";
  }

  if (options.isSameView) {
    return options.currentRoute.panel;
  }

  // Cross-view: never inherit node-panel open state. Thread view uses
  // ``isNodePanelOpen`` for ``ThreadPanelLayout``; every other view uses it
  // for ``NodePanelLayout`` — different UIs sharing one flag. Restoring a
  // remembered ``panelNodeId`` without an explicit open intent would show an
  // empty details panel or animate one panel closed and another open.
  if (targetView === "thread") {
    return "none";
  }

  // On a phone the thread would cover triage's queue: it opens with nothing over it.
  if (targetView === "triage" && isPhoneWidth()) {
    return "none";
  }

  const { isThreadPanelOpen } = State.getState();
  return isThreadPanelOpen ? "thread" : "none";
}

export type AppRouteState = {
  view: AppViewsType;
  tab: AppViewTabModeType | null;
  /**
   * ``identifier`` of the record shown by this view's routed detail page —
   * the trailing path segment of ``/app/documents/all/<identifier>``. The
   * typename comes from ``detailTypenameForView``; the pair is stored as a
   * global id in ``activeTabNodeIds[view][tab]``, the same slot rules and
   * reports use for their detail records.
   */
  detailIdentifier: string | null;
  ruleId: string | null;
  viewId: string | null;
  panel: PanelStateType;
  panelNodeId: string | null;
  panelStack: string[];
  filters: FilterType[];
  sortables: SortableType[];
  viewMode: ViewMode;
};

const DEFAULT_APP_VIEW: AppViewsType = "triage";
/** Detail segments are record identifiers; tab segments are words. */
const DETAIL_IDENTIFIER_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DEFAULT_PATHNAME = "/app/triage";
const ROUTE_VALUE_SEPARATOR = "~";
const VIEW_SEGMENTS: Record<AppViewsType, string> = {
  thread: "threads",
  "financial-accounts": "financial-accounts",
  "chart-of-accounts": "chart-of-accounts",
  contacts: "contacts",
  organizations: "organizations",
  documents: "documents",
  "journal-entries": "journal-entries",
  "tax-codes": "tax-codes",
  triage: "triage",
  budget: "budget",
  reports: "reports",
  transactions: "transactions",
  rules: "rules",
};

const SEGMENT_TO_VIEW = Object.entries(VIEW_SEGMENTS).reduce(
  (acc, [view, segment]) => {
    acc[segment] = view as AppViewsType;
    return acc;
  },
  {} as Record<string, AppViewsType>,
);

const DEFAULT_VIEW_MODES: Partial<Record<AppViewsType, ViewMode>> = {
  "financial-accounts": "card",
  reports: "card",
};

export function getDefaultTab(view: AppViewsType): AppViewTabModeType | null {
  const values = Values.APP_VIEW_TAB_MODE_MAP[view]?.values ?? [];
  return (values[0] as AppViewTabModeType | undefined) ?? null;
}

// A list opens as cards on a phone - a table's columns don't fit across one -
// and as each view has it elsewhere.
function getDefaultViewMode(view: AppViewsType): ViewMode {
  return isPhoneWidth() ? "card" : (DEFAULT_VIEW_MODES[view] ?? "table");
}

function isValidTab(
  view: AppViewsType,
  tab: string | null | undefined,
): tab is AppViewTabModeType {
  if (!tab) return false;
  return (Values.APP_VIEW_TAB_MODE_MAP[view]?.values ?? []).includes(tab);
}

function parseDelimitedTuple(
  rawValue: string,
  expectedParts: number,
  separator: string = ROUTE_VALUE_SEPARATOR,
): string[] | null {
  let value = rawValue;
  const parts: string[] = [];

  for (let index = 0; index < expectedParts - 1; index += 1) {
    const separatorIndex = value.indexOf(separator);
    if (separatorIndex < 0) {
      return null;
    }

    parts.push(value.slice(0, separatorIndex));
    value = value.slice(separatorIndex + separator.length);
  }

  parts.push(value);
  return parts;
}

function encodeUrlSafeValue(value: unknown): string {
  const json = JSON.stringify(value ?? null);
  const bytes = new TextEncoder().encode(json);
  let binary = "";

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

function decodeUrlSafeValue(value: string): unknown {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  const json = new TextDecoder().decode(bytes);

  return JSON.parse(json);
}

function serializeFilter(filter: FilterType): string {
  return [
    filter.key,
    filter.type,
    String(filter.operator ?? ""),
    encodeUrlSafeValue(filter.value),
    encodeUrlSafeValue(filter.isLocked === true),
  ].join(ROUTE_VALUE_SEPARATOR);
}

function deserializeFilter(rawValue: string): FilterType | null {
  const parts =
    parseDelimitedTuple(rawValue, 5) ?? parseDelimitedTuple(rawValue, 5, ":");
  if (!parts) {
    return null;
  }

  const [key, type, operator, serializedValue, isLocked] = parts;

  try {
    const decodedIsLocked =
      isLocked === "1" || isLocked === "0"
        ? isLocked === "1"
        : Boolean(decodeUrlSafeValue(isLocked));

    return {
      key,
      type: type as FilterType["type"],
      operator: (operator || null) as FilterType["operator"],
      value: rawValue.includes(ROUTE_VALUE_SEPARATOR)
        ? decodeUrlSafeValue(serializedValue)
        : JSON.parse(serializedValue),
      ...(decodedIsLocked ? { isLocked: true } : {}),
    } as FilterType;
  } catch {
    return null;
  }
}

function serializeSortable(sortable: SortableType): string {
  return [sortable.key, sortable.value].join(ROUTE_VALUE_SEPARATOR);
}

function deserializeSortable(rawValue: string): SortableType | null {
  const parts =
    parseDelimitedTuple(rawValue, 2) ?? parseDelimitedTuple(rawValue, 2, ":");
  if (!parts) {
    return null;
  }

  const [key, value] = parts;
  if (!key || !value) {
    return null;
  }

  return { key, value };
}

function parsePathname(
  pathname: string,
): Pick<AppRouteState, "view" | "tab" | "ruleId" | "detailIdentifier"> {
  const withoutBase = pathname.replace(/^\/app\/?/, "");
  const segments = withoutBase.split("/").filter(Boolean);
  const firstSegment = segments[0];

  if (firstSegment === "overview" || firstSegment === "views") {
    return {
      view: "reports",
      tab: getDefaultTab("reports"),
      ruleId: null,
      detailIdentifier: null,
    };
  }

  const legacyReportTabs = new Set([
    "cash-flow",
    "profit-loss",
    "balance-sheet",
  ]);
  if (firstSegment === "reports" && legacyReportTabs.has(segments[1] ?? "")) {
    return {
      view: "reports",
      tab: getDefaultTab("reports"),
      ruleId: null,
      detailIdentifier: null,
    };
  }

  if (!firstSegment) {
    return {
      view: DEFAULT_APP_VIEW,
      tab: getDefaultTab(DEFAULT_APP_VIEW),
      ruleId: null,
      detailIdentifier: null,
    };
  }

  const view = SEGMENT_TO_VIEW[firstSegment] ?? DEFAULT_APP_VIEW;
  const secondSegment = segments[1];
  const defaultTab = getDefaultTab(view);
  // A detail page is the trailing identifier segment — after the tab where the
  // view has tabs (``/app/documents/all/<id>``), directly after the view where
  // it does not (``/app/journal-entries/<id>``).
  const lastSegment = segments[segments.length - 1];
  const detailIdentifier =
    detailTypenameForView(view) != null &&
    segments.length > 1 &&
    DETAIL_IDENTIFIER_PATTERN.test(lastSegment)
      ? lastSegment
      : null;

  if ((Values.APP_VIEW_TAB_MODE_MAP[view]?.values?.length ?? 0) > 0) {
    return {
      view,
      tab: isValidTab(view, secondSegment) ? secondSegment : defaultTab,
      ruleId: null,
      detailIdentifier,
    };
  }

  return {
    view,
    tab: defaultTab,
    ruleId: null,
    detailIdentifier,
  };
}

function getPathnameFromUrl(url: string): string {
  const queryIndex = url.indexOf("?");
  return queryIndex >= 0 ? url.slice(0, queryIndex) : url;
}

export function parseAppRoute(pathname: string, search: string): AppRouteState {
  const pathState = parsePathname(pathname);
  const searchParams = new URLSearchParams(search);
  const rawPanel = searchParams.get("panel") as LegacyPanelStateType | null;
  // The slide-out thread panel is independent of the Threads *view* route.
  // Default to ``none`` on ``/app/threads`` so navigation does not imply
  // ``isThreadPanelOpen`` - and on triage, where the app opens, so the chat
  // is opened only when asked for.
  const defaultPanel: PanelStateType =
    pathState.view === "thread" || pathState.view === "triage" || isPhoneWidth()
      ? "none"
      : "thread";
  // Migrate legacy values onto the two-panel model.
  const panel: PanelStateType =
    rawPanel == null
      ? defaultPanel
      : rawPanel === "hidden"
        ? "none"
        : rawPanel === "expanded"
          ? "both"
          : rawPanel === "node" ||
              rawPanel === "thread" ||
              rawPanel === "both" ||
              rawPanel === "none"
            ? rawPanel
            : defaultPanel;
  const panelNodeId = searchParams.get("nodeId");
  const panelStack = (searchParams.get("panelStack") ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  const filters = searchParams
    .getAll("filter")
    .map(deserializeFilter)
    .filter((filter): filter is FilterType => filter != null);
  const sortables = searchParams
    .getAll("sort")
    .map(deserializeSortable)
    .filter((sortable): sortable is SortableType => sortable != null);
  const viewMode = searchParams.get("viewMode");
  const ruleId = searchParams.get("ruleId") ?? searchParams.get("workflowId");
  const viewId = searchParams.get("viewId");

  return {
    ...pathState,
    ruleId: pathState.view === "rules" ? ruleId : null,
    viewId: pathState.view === "reports" ? viewId : null,
    panel,
    panelNodeId,
    panelStack,
    filters,
    sortables,
    viewMode:
      viewMode === "card" || viewMode === "table"
        ? viewMode
        : getDefaultViewMode(pathState.view),
  };
}

export function buildAppUrl(routeState: AppRouteState): string {
  const searchParams = new URLSearchParams();
  const viewSegment =
    VIEW_SEGMENTS[routeState.view] ?? VIEW_SEGMENTS[DEFAULT_APP_VIEW];
  const tab = isValidTab(routeState.view, routeState.tab)
    ? routeState.tab
    : getDefaultTab(routeState.view);

  let pathname = `/app/${viewSegment}`;

  if (
    (Values.APP_VIEW_TAB_MODE_MAP[routeState.view]?.values?.length ?? 0) > 0 &&
    tab
  ) {
    pathname = `/app/${viewSegment}/${tab}`;
  }

  const detailIdentifier =
    detailTypenameForView(routeState.view) != null
      ? routeState.detailIdentifier
      : null;
  if (detailIdentifier) {
    pathname = `${pathname}/${detailIdentifier}`;
  }

  routeState.sortables.forEach((sortable) => {
    searchParams.append("sort", serializeSortable(sortable));
  });

  routeState.filters.forEach((filter) => {
    if (filter.isLocked === true) {
      return;
    }
    searchParams.append("filter", serializeFilter(filter));
  });

  if (routeState.viewMode !== getDefaultViewMode(routeState.view)) {
    searchParams.set("viewMode", routeState.viewMode);
  }

  if (routeState.view === "rules" && routeState.ruleId) {
    searchParams.set("ruleId", routeState.ruleId);
  }

  if (routeState.view === "reports" && routeState.viewId) {
    searchParams.set("viewId", routeState.viewId);
  }

  if (routeState.panelNodeId) {
    searchParams.set("nodeId", routeState.panelNodeId);
  }

  if (routeState.panelStack.length > 0) {
    searchParams.set("panelStack", routeState.panelStack.join(","));
  }

  searchParams.set("panel", routeState.panel);

  const search = searchParams.toString();
  return search ? `${pathname}?${search}` : pathname;
}

function routeStateFromStore(state: GlobalStoreStateType): AppRouteState {
  const view = state.activeAppView ?? DEFAULT_APP_VIEW;
  const tab = isValidTab(view, state.activeTab)
    ? state.activeTab
    : getDefaultTab(view);
  // Detail records live in ``activeTabNodeIds`` as global ids; the URL shows
  // only the identifier half. Clearing that slot (e.g. the breadcrumb's
  // "back to list") therefore drops the detail segment from the URL.
  const detailNodeId =
    detailTypenameForView(view) != null
      ? ((
          state.activeTabNodeIds as Record<
            string,
            Record<string, string | null> | undefined
          >
        )?.[view]?.[tab as unknown as string] ?? null)
      : null;
  // The Node panel can be open *without* a selected id — it renders an
  // empty "Select an item…" state. Encoding requires only the user-intent
  // bit; ``panelNodeId`` is serialized separately.
  const nodeOpen = state.isNodePanelOpen;
  const threadOpen = view === "thread" ? false : state.isThreadPanelOpen;
  const panel: PanelStateType =
    nodeOpen && threadOpen
      ? "both"
      : nodeOpen
        ? "node"
        : threadOpen
          ? "thread"
          : "none";
  return {
    view,
    tab,
    detailIdentifier: detailNodeId ? idToIdentifier(detailNodeId) : null,
    ruleId:
      view === "rules"
        ? ((state.activeTabNodeIds as any)?.rules?.[tab as any] ?? null)
        : null,
    viewId:
      view === "reports"
        ? ((
            state.activeTabNodeIds as {
              reports?: Record<string, string | null>;
              views?: Record<string, string | null>;
            }
          )?.reports?.[getReportsTabKey(tab)] ??
          (
            state.activeTabNodeIds as {
              views?: Record<string, string | null>;
            }
          )?.views?.[getReportsTabKey(tab)] ??
          null)
        : null,
    panel,
    panelNodeId: state.activePanelNodeId,
    panelStack: state.queuedPanelNodeIds,
    filters: state.filters,
    sortables: state.sortables,
    viewMode: state.dataViewModes[view] ?? getDefaultViewMode(view),
  };
}

function routeStateToStorePatch(
  routeState: AppRouteState,
  currentState: GlobalStoreStateType,
): Partial<
  CoreStateType & Pick<GlobalStoreStateType, "filters" | "sortables">
> {
  const detailTypename = detailTypenameForView(routeState.view);
  return {
    previousAppView:
      currentState.activeAppView !== routeState.view
        ? currentState.activeAppView
        : currentState.previousAppView,
    activeAppView: routeState.view,
    activeTab: (routeState.tab ??
      getDefaultTab(routeState.view)) as AppViewTabModeType,
    activeTabNodeIds: {
      ...currentState.activeTabNodeIds,
      ...(routeState.view === "rules"
        ? {
            rules: {
              ...(((currentState.activeTabNodeIds as any)?.rules ||
                {}) as Record<string, string | null>),
              [routeState.tab as any]: routeState.ruleId,
            },
          }
        : {}),
      ...(routeState.view === "reports"
        ? {
            reports: {
              ...(((currentState.activeTabNodeIds as any)?.reports ||
                {}) as Record<string, string | null>),
              [getReportsTabKey(routeState.tab)]: routeState.viewId,
            },
          }
        : {}),
      ...(detailTypename
        ? {
            [routeState.view]: {
              ...(((currentState.activeTabNodeIds as any)?.[routeState.view] ||
                {}) as Record<string, string | null>),
              [routeState.tab as any]: routeState.detailIdentifier
                ? identifierAndTypenameToId(
                    routeState.detailIdentifier,
                    detailTypename,
                  )
                : null,
            },
          }
        : {}),
    },
    activePanelNodeIds: {
      ...currentState.activePanelNodeIds,
      [routeState.view]: routeState.panelNodeId,
    },
    activePanelNodeId: routeState.panelNodeId,
    queuedPanelNodeIds: routeState.panelStack,
    isNodePanelOpen: routeState.panel === "node" || routeState.panel === "both",
    isThreadPanelOpen:
      routeState.view === "thread"
        ? currentState.isThreadPanelOpen
        : routeState.panel === "thread" || routeState.panel === "both",
    dataViewModes: {
      ...currentState.dataViewModes,
      [routeState.view]: routeState.viewMode,
    },
    filters: routeState.filters,
    sortables: routeState.sortables,
  };
}

export function useAppRouteState(): AppRouteState {
  const location = useLocation();

  return useMemo(
    () => parseAppRoute(location.pathname, location.search),
    [location.pathname, location.search],
  );
}

export function useAppRouteNavigation() {
  const navigate = useNavigate();
  const routeState = useAppRouteState();
  const currentPathname = getPathnameFromUrl(buildAppUrl(routeState));

  return useMemo(
    () => ({
      navigateTo(
        nextState: Partial<AppRouteState>,
        options?: { replace?: boolean },
      ) {
        const nextUrl = buildAppUrl({
          ...routeState,
          ...nextState,
        });
        const shouldReplace =
          options?.replace ?? getPathnameFromUrl(nextUrl) === currentPathname;

        navigate(nextUrl, {
          replace: shouldReplace,
        });
      },
      navigateToView(
        view: AppViewsType,
        options?: {
          tab?: AppViewTabModeType | null;
          ruleId?: string | null;
          viewId?: string | null;
          /** Land on this record's detail page instead of the view's list. */
          detailIdentifier?: string | null;
          preservePanel?: boolean;
          preserveQuery?: boolean;
          replace?: boolean;
        },
      ) {
        // The Node panel id is stored *per view* in
        // ``activePanelNodeIds[view]``. When navigating to a different
        // view, prefer that view's remembered id over the current view's
        // (which would otherwise leak a workflow node id into transactions
        // etc.). Same view → keep the current id. ``preservePanel: false``
        // wipes panel state entirely.
        const isSameView = view === routeState.view;
        const rememberedNodeId =
          State.getState().activePanelNodeIds[view] ?? null;
        const storePanelNodeId = State.getState().activePanelNodeId;
        const nextPanelNodeId =
          options?.preservePanel === false
            ? null
            : view === "reports" && options?.viewId
              ? options.viewId
              : isSameView
                ? (storePanelNodeId ?? routeState.panelNodeId)
                : rememberedNodeId;
        const nextRouteState: AppRouteState = {
          view,
          tab: options?.tab ?? getDefaultTab(view),
          // Navigating to a view lands on its list unless the caller names a
          // record — a remembered detail is never restored implicitly.
          detailIdentifier: options?.detailIdentifier ?? null,
          ruleId: options?.ruleId ?? null,
          viewId: options?.viewId ?? null,
          panel: resolveNavigatePanel(view, {
            preservePanel: options?.preservePanel,
            isSameView,
            currentRoute: routeState,
          }),
          panelNodeId: nextPanelNodeId,
          panelStack:
            options?.preservePanel === false ? [] : routeState.panelStack,
          filters: options?.preserveQuery ? routeState.filters : [],
          sortables: options?.preserveQuery ? routeState.sortables : [],
          viewMode: options?.preserveQuery
            ? routeState.viewMode
            : getDefaultViewMode(view),
        };

        const nextUrl = buildAppUrl(nextRouteState);
        const shouldReplace =
          options?.replace ?? getPathnameFromUrl(nextUrl) === currentPathname;

        navigate(nextUrl, {
          replace: shouldReplace,
        });
      },
    }),
    [currentPathname, navigate, routeState],
  );
}

export function useSyncAppRouteState() {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = useAppRouteState();
  const currentUrl = `${location.pathname}${location.search}`;
  const canonicalUrl = buildAppUrl(routeState);
  // A page that is none of the views - settings, the invitations, the
  // accountant's clients - keeps its own address: only a view's state is
  // written into its URL, and parsed, anything else read as the default view.
  const isPageOfItsOwn = !(
    (location.pathname.split("/")[2] ?? "") in SEGMENT_TO_VIEW
  );
  const isApplyingRouteRef = useRef(false);
  const hasSyncedRouteRef = useRef(false);

  const activeAppView = State((state) => state.activeAppView);
  const activeTab = State((state) => state.activeTab);
  const activeTabNodeIds = State((state) => state.activeTabNodeIds);
  const activePanelNodeId = State((state) => state.activePanelNodeId);
  const queuedPanelNodeIds = State((state) => state.queuedPanelNodeIds);
  const isNodePanelOpen = State((state) => state.isNodePanelOpen);
  const isThreadPanelOpen = State((state) => state.isThreadPanelOpen);
  const filters = State((state) => state.filters);
  const sortables = State((state) => state.sortables);
  const dataViewModes = State((state) => state.dataViewModes);

  useEffect(() => {
    if (isPageOfItsOwn) {
      return;
    }
    if (currentUrl !== canonicalUrl) {
      navigate(canonicalUrl, { replace: true });
    }
  }, [canonicalUrl, currentUrl, isPageOfItsOwn, navigate]);

  useLayoutEffect(() => {
    if (isPageOfItsOwn) {
      return;
    }
    const currentState = State.getState();
    const fullPatch = routeStateToStorePatch(routeState, currentState);

    const patch: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(fullPatch)) {
      if (!_.isEqual(value, (currentState as Record<string, unknown>)[key])) {
        patch[key] = value;
      }
    }

    isApplyingRouteRef.current = true;
    if (Object.keys(patch).length > 0) {
      State.setState(patch);
    }
    hasSyncedRouteRef.current = true;

    queueMicrotask(() => {
      isApplyingRouteRef.current = false;
    });
  }, [canonicalUrl, isPageOfItsOwn, routeState]);

  useEffect(() => {
    if (isPageOfItsOwn) {
      return;
    }
    if (!hasSyncedRouteRef.current || isApplyingRouteRef.current) {
      return;
    }

    const nextUrl = buildAppUrl(
      (() => {
        const nextStoreRouteState = routeStateFromStore({
          ...State.getState(),
          activeAppView,
          activeTab,
          activeTabNodeIds,
          activePanelNodeId,
          queuedPanelNodeIds,
          isNodePanelOpen,
          isThreadPanelOpen,
          filters,
          sortables,
          dataViewModes,
        });

        return {
          ...routeState,
          detailIdentifier: nextStoreRouteState.detailIdentifier,
          ruleId: nextStoreRouteState.ruleId,
          viewId:
            nextStoreRouteState.viewId ??
            (routeState.view === "reports" ? routeState.viewId : null),
          panel: nextStoreRouteState.panel,
          panelNodeId: nextStoreRouteState.panelNodeId,
          panelStack: nextStoreRouteState.panelStack,
          filters: nextStoreRouteState.filters,
          sortables: nextStoreRouteState.sortables,
          viewMode: nextStoreRouteState.viewMode,
        };
      })(),
    );

    if (nextUrl !== canonicalUrl) {
      navigate(nextUrl, {
        replace: getPathnameFromUrl(nextUrl) === getPathnameFromUrl(currentUrl),
      });
    }
  }, [
    activeAppView,
    activePanelNodeId,
    activeTab,
    activeTabNodeIds,
    canonicalUrl,
    dataViewModes,
    filters,
    isNodePanelOpen,
    isThreadPanelOpen,
    currentUrl,
    navigate,
    queuedPanelNodeIds,
    sortables,
    isPageOfItsOwn,
  ]);
}

export function AppIndexRedirect() {
  return (
    <Navigate to={buildAppUrl(routeStateFromStore(State.getState()))} replace />
  );
}

export function AppFallbackRedirect() {
  return <Navigate to={DEFAULT_PATHNAME} replace />;
}

/**
 * Open a record: its detail page where its kind has one, otherwise the
 * fullscreen dialog (accounts, and anything else without a page). What a
 * reference chip does when clicked, its popover's open button, a card's, and
 * a rule's row in the rules list.
 */
export function useOpenRecord() {
  const appRouteNavigation = useAppRouteNavigation();
  const routeState = useAppRouteState();
  return useCallback(
    (
      record: { id: string; identifier?: string; entityId?: string },
      origin?: HTMLElement,
    ) => {
      const typename = idToTypename(record.id);

      // A rule's page is its editor, reached by the rule's id in the rules
      // view rather than by the generic detail route.
      if (typename === "RuleDefinitionObject") {
        const { activeTab, activeTabNodeIds, setActiveTabNodeIds } =
          State.getState();
        setActiveTabNodeIds({
          ...activeTabNodeIds,
          rules: {
            ...(((activeTabNodeIds as Record<string, unknown>)?.rules ||
              {}) as Record<string, string | null>),
            [activeTab]: record.id,
          },
        });
        State.setState({ activeAppView: "rules" });
        appRouteNavigation.navigateToView("rules", {
          ruleId: record.id,
          preservePanel: true,
          preserveQuery: true,
          // The page is the list's own path with the rule in the query, which
          // would otherwise stand in for the list's step in the history and
          // leave the back button nowhere to return to.
          replace: false,
        });
        return;
      }

      const detailView = detailViewForTypename(typename);
      const detailIdentifier = record.identifier || idToIdentifier(record.id);

      if (detailView && detailIdentifier) {
        // Same view: keep the list's tab and filters so returning lands back
        // where the user was. Different view: enter it on the record.
        if (detailView === routeState.view) {
          // Opened from a list-table cell: remember it, so returning to the
          // list puts focus back on it. Anywhere else clears a previous one.
          const originNav = origin
            ?.closest("[data-grid-cell]")
            ?.querySelector<HTMLElement>("[data-col-index]");
          State.setState({
            detailOriginCell: originNav
              ? {
                  rowId: record.id,
                  colIndex: Number(originNav.dataset.colIndex),
                }
              : null,
          });
          appRouteNavigation.navigateTo({ detailIdentifier });
        } else {
          appRouteNavigation.navigateToView(detailView, { detailIdentifier });
        }
        return;
      }

      const entityIdentifier = record.entityId
        ? idToIdentifier(record.entityId)
        : State.getState().getEntityIdentifier();
      State.getState().pushToCommandStack({
        commandDefinitionId: `update-${typename}`,
        context: {
          nodeId: record.id,
          entityIdentifier,
        },
      });
    },
    [appRouteNavigation, routeState.view],
  );
}
